import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import type { ChainHealth } from "@clustec/common";
import { theme } from "../lib/theme";

/**
 * Below this width the strip says the same things in less space. It stays four stages across at
 * every width — read left to right, that row *is* the pipeline, and no stacking of it says
 * "proposed, then checkpointed, then proven, then finalized" half as well.
 *
 * Nothing here is renamed or dropped; every number and every word the desktop shows is still shown.
 * Spelled out the row needs 356px against the 286px a 320px phone leaves it, and what gives is
 * spacing and type size. The one real saving in the text is "Checkpoint", which was written above
 * all four checkpoint numbers — a column heading repeated four times, and the reason that line
 * wrapped to two rows on a phone. "chk" keeps the cue at a fifth of the width.
 *
 * A container query rather than a media query: the strip is only ever as wide as the card holding
 * it, and on the dashboard that card is a third of the top row — 359px on a 900px desktop window,
 * narrower than the same strip gets on a 430px phone. The viewport cannot answer the question being
 * asked here. 440px then puts every phone on the same side of the line, and the widest of them
 * leaves the strip 396px.
 */
const CONDENSE = "@container (max-width: 440px)";

/**
 * Inside this many slots of the deadline, the note names the slot the prune would happen at.
 * Eight slots is ~10 minutes on both our networks — long enough to be worth reading and short
 * enough that saying so isn't crying wolf.
 */
const CRITICAL_SLOTS = 8;

export type ProofWindowTone = "ok" | "warn" | "bad";

/**
 * A window with more than one epoch of slack left is unremarkable and stays grey. The pending
 * chain normally carries roughly an epoch of unproven work, so colouring that state would light
 * the strip up permanently — and a warning that is always on is one nobody reads.
 */
export function proofWindowTone(headroomSlots: number, slotsPerEpoch: number): ProofWindowTone {
  if (headroomSlots <= CRITICAL_SLOTS) return "bad";
  if (headroomSlots <= slotsPerEpoch) return "warn";
  return "ok";
}

/**
 * The proof-window note: how much of the rollup's proof deadline is left before it may prune the
 * pending chain back to the last proven checkpoint, discarding every checkpoint after it.
 *
 * Written as a capacity reading rather than a countdown to an event. "Prune in 33 slots" says a
 * prune is coming; "proof window · 33 of 64 slots left" says the same arithmetic about a quantity
 * that always has a value and is usually fine. The word *prune* is held back until the deadline
 * is actually close, where it earns the attention it takes.
 *
 * Returns null when there is nothing honest to say — either the network's proof window hasn't
 * been read from L1 yet, or we have no genesis time to locate the current slot against. The
 * caller renders nothing, which is exactly today's behaviour.
 *
 * `lead` and `tail` are always muted; `figure` is the only part that ever takes colour, so the
 * line reads as one quiet sentence with at most one number raised out of it.
 */
export function proofWindowNote(
  proofWindow: ChainHealth["proofWindow"],
  currentSlot: number | null,
): {
  tone: ProofWindowTone;
  lead: string;
  figureLong: string;
  figureShort: string;
  tailLong: string;
  tailShort: string;
} | null {
  if (proofWindow == null || currentSlot == null) return null;
  const { windowSlots, deadlineSlot, proofSubmissionEpochs } = proofWindow;

  // Nothing pending means nothing to lose. Said out loud rather than by hiding the line: a note
  // that appears only when something is wrong is an alarm by its mere presence, which is the
  // thing this note is deliberately not.
  if (deadlineSlot == null) {
    return {
      tone: "ok",
      lead: "proof window ·",
      figureLong: "all proven",
      figureShort: "all proven",
      tailLong: "",
      tailShort: "",
    };
  }

  // Derived, not passed: windowSlots is (proofSubmissionEpochs + 1) epochs, so the epoch length
  // falls out of it and the component needs one prop fewer to stay in step with the server.
  const slotsPerEpoch = windowSlots / (proofSubmissionEpochs + 1);
  const headroomSlots = deadlineSlot - currentSlot;

  // The deadline can pass before the prune transaction actually lands — on 2026-08-14 the
  // deadline slot was 40832 and the prune executed during 40832–40833 — so a negative headroom
  // is a real state to render, not an impossible one to clamp away.
  if (headroomSlots <= 0) {
    return {
      tone: "bad",
      lead: "proof window ·",
      figureLong: "closed",
      figureShort: "closed",
      tailLong: "· prune due",
      tailShort: "· prune due",
    };
  }

  const tone = proofWindowTone(headroomSlots, slotsPerEpoch);
  return {
    tone,
    lead: "proof window ·",
    figureLong: `${headroomSlots} of ${windowSlots} slots left`,
    figureShort: `${headroomSlots}/${windowSlots}`,
    tailLong: tone === "bad" ? `· prune at slot ${deadlineSlot.toLocaleString()}` : "",
    tailShort: tone === "bad" ? `· prune at ${deadlineSlot.toLocaleString()}` : "",
  };
}

/** Owns the width the container query measures. `Wrapper` cannot: a query reads its own container,
 *  never itself. */
const Sizer = styled.div`
  container-type: inline-size;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: stretch;
  gap: 2px;
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  /* The floor under the condensing above, and never reached at a width the app renders at. Block
     numbers gain a digit every so often, and when this one day outgrows the narrowest phone it
     should pan rather than run off the side of the card unreachably. */
  overflow-x: auto;
  overscroll-behavior-x: contain;

  ${CONDENSE} {
    gap: 1px;
  }
`;

const Stage = styled.div<{ active: boolean; color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex: 1;
  min-width: 0;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${(p) => (p.active ? `${p.color}15` : theme.colors.bg)};
  border: 1px solid ${(p) => (p.active ? `${p.color}40` : theme.colors.border)};
  border-radius: ${theme.radius.sm};

  ${CONDENSE} {
    /* 8px of side padding on each side of four boxes is 64px of the 286px a 320px phone has. */
    padding: ${theme.spacing.xs} 1px;
  }
`;

const StageLabel = styled.span`
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.textMuted};
  text-align: center;
  white-space: nowrap;

  ${CONDENSE} {
    /* "CHECKPOINTED" is twelve characters against "PROVEN"'s six, so it alone decides how much type
       the row can afford — every box is the same width, so what the longest name cannot fit, none of
       them can. Rather than rewrite the name to suit the box, the type shrinks to suit the name: the
       tracking goes first, since 0.05em over twelve characters is 5px of pure air, and cqi units take
       the size down only as far as the card actually requires. A 360px phone still reads them at the
       full 9px; only narrower cards go below it, and all four shrink together so they stay a set. */
    letter-spacing: 0;
    font-size: clamp(7px, 2.75cqi, 9px);
  }
`;

const StageBlock = styled.span<{ color: string }>`
  font-weight: 600;
  color: ${(p) => p.color};
  /* primary unit: the block number — block is the only stage cursor we always
     have, regardless of where the chain is in the checkpoint/epoch cycle. */
  /* text-align matters on mobile where narrow stages wrap "Block #1,234,567"
     to two lines; align-items: center on the column centers the span as a
     flex item but doesn't affect the text flow inside it. */
  text-align: center;
`;

/*
 * The word above every checkpoint number, shortened rather than dropped. Four copies of it is a
 * column heading written four times, and it is what forced this line to wrap to two rows on a phone
 * — losing it is what takes the strip from 60px tall back to the 49px it has on a desktop. But a
 * bare "#33,180" under a bold "#37,550" is two unlabelled numbers to anyone who only ever sees the
 * phone layout, so three characters stay to say which is which.
 */
const MetaNoun = styled.span`
  ${CONDENSE} {
    display: none;
  }
`;

const MetaNounShort = styled.span`
  display: none;

  ${CONDENSE} {
    display: inline;
  }
`;

const StageMeta = styled.span`
  font-size: 9px;
  color: ${theme.colors.textMuted};
  line-height: 1.3;
  text-align: center;
  white-space: nowrap;
  /* checkpoint + epoch belong to the same block; surface them so users can
     tell at a glance which unit each stage operates on (block #, contained
     in checkpoint #, in epoch #). */
`;

const Gap = styled.span<{ severity: "ok" | "warn" | "bad" }>`
  font-size: 9px;
  align-self: center;
  color: ${(p) =>
    p.severity === "ok"
      ? theme.colors.textMuted
      : p.severity === "warn"
        ? theme.colors.warning
        : theme.colors.danger};
  flex-shrink: 0;
  padding: 0 2px;

  ${CONDENSE} {
    /* The lag markers sit between the boxes, so every pixel they give up is a pixel of name. */
    padding: 0;
    font-size: 8px;
  }
`;

/**
 * The proof-window note. A sibling of `Wrapper` inside `Sizer`, which is load-bearing in both
 * directions: inside `Wrapper` it would join that element's horizontal scroll and slide out of
 * view with the row, and outside `Sizer` it would fall outside the container query and never
 * condense.
 *
 * It goes below the row rather than into the lag marker between Checkpointed and Proven, which is
 * where a countdown most obviously belongs and is the one place it cannot go. Spelled out the row
 * already needs 356px against the 286px a 320px phone leaves it — the markers are down to
 * `padding: 0` and 8px type for exactly that reason — so anything added between the stages
 * re-breaks the wrap the condensing above exists to prevent. Below costs 15px of height at 320px
 * — measured on the running dashboard, 48px of strip becoming 63px — and nothing at all of width.
 */
const Note = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 4px;
  margin-top: 4px;
  font-size: 9px;
  font-family: monospace;
  line-height: 1.35;
  color: ${theme.colors.textMuted};

  ${CONDENSE} {
    /* Same treatment as StageLabel: the tracking and then the size, only as far as needed. */
    font-size: clamp(8px, 2.6cqi, 9px);
  }
`;

/** The one part of the note that ever carries colour — see {@link proofWindowNote}. */
const NoteFigure = styled.span<{ tone: ProofWindowTone }>`
  color: ${(p) =>
    p.tone === "ok"
      ? theme.colors.textMuted
      : p.tone === "warn"
        ? theme.colors.warning
        : theme.colors.danger};
`;

/** Long form on a roomy card, short form on a narrow one. Same pattern as MetaNoun above. */
const NoteLong = styled.span`
  ${CONDENSE} {
    display: none;
  }
`;

const NoteShort = styled.span`
  display: none;

  ${CONDENSE} {
    display: inline;
  }
`;

/**
 * How often the note re-derives the current slot.
 *
 * It needs its own clock. The health payload arrives over the websocket on new blocks, and
 * `useChainHealth` disables polling entirely while that socket is connected — so a chain that has
 * stopped producing stops refreshing this component, which is precisely the situation the note
 * exists to describe. Ten seconds against a slot of 36–72s keeps a whole-slot figure honest
 * without re-rendering 72 times per change the way a 1s tick would.
 */
const TICK_MS = 10_000;

/**
 * Severity for the lag markers between stages.
 *
 * The checkpointed → proven gap is deliberately exempt: it is the one gap whose real threshold is
 * the proof window, and the note below the row now states that in the protocol's own terms. Before,
 * this function coloured it red past 15 blocks — a number with no meaning in the rollup — which on
 * testnet is most of the time, since the proven cursor routinely trails by ~30. So the strip carried
 * a permanent red alarm beside a note saying, correctly, that there was half a window of slack left.
 * Two voices on one fact, and the louder one was the one making it up.
 *
 * The block counts themselves still render, in muted type. They are useful as depth; they were never
 * evidence of anything being wrong.
 */
function gapSeverity(gap: number, exempt = false): "ok" | "warn" | "bad" {
  if (exempt) return "ok";
  if (gap <= 5) return "ok";
  if (gap <= 15) return "warn";
  return "bad";
}

export function FinalityPipeline({
  proposedBlock,
  proposedCheckpoint,
  proposedEpoch,
  checkpointedBlock,
  checkpointedCheckpoint,
  checkpointedEpoch,
  provenBlock,
  provenCheckpoint,
  provenEpoch,
  finalizedBlock,
  finalizedCheckpoint,
  finalizedEpoch,
  proofWindow,
  genesisTime,
  slotDuration,
}: {
  proposedBlock: number;
  proposedCheckpoint: number | null;
  proposedEpoch: number | null;
  checkpointedBlock: number;
  checkpointedCheckpoint: number | null;
  checkpointedEpoch: number | null;
  provenBlock: number;
  provenCheckpoint: number | null;
  provenEpoch: number | null;
  finalizedBlock: number;
  finalizedCheckpoint: number | null;
  finalizedEpoch: number | null;
  /**
   * The rollup's proof deadline for the pending chain. Optional on purpose: the socket pushes
   * health payloads straight from whichever server process is running, so during a rolling
   * restart one that predates this field will send none — and the note is better absent than
   * rendered against a window we don't have. Same reasoning as `sparklines.blockCounts` on the
   * dashboard card.
   */
  proofWindow?: ChainHealth["proofWindow"];
  genesisTime?: number | null;
  slotDuration?: number | null;
}) {
  // Ticks so the countdown counts down between health payloads; see TICK_MS.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(interval);
  }, []);

  const currentSlot =
    genesisTime != null && slotDuration != null && slotDuration > 0
      ? Math.floor((Math.floor(now / 1000) - genesisTime) / slotDuration)
      : null;
  const note = proofWindowNote(proofWindow ?? null, currentSlot);

  const stages = [
    // Proposed is fundamentally block-level — the head block is often still
    // in-flight to a checkpoint, so checkpoint may be null. We still surface
    // the epoch (derived from slot) so the user can locate it on the timeline.
    {
      label: "Proposed",
      block: proposedBlock,
      checkpoint: proposedCheckpoint,
      epoch: proposedEpoch,
      color: theme.colors.accent,
    },
    // Checkpointed = the last block whose containing checkpoint landed on L1.
    // Checkpoint number is the unit that actually moved.
    {
      label: "Checkpointed",
      block: checkpointedBlock,
      checkpoint: checkpointedCheckpoint,
      epoch: checkpointedEpoch,
      color: theme.colors.primary,
    },
    // Proven / Finalized are checkpoint-level concepts on Aztec — these stages
    // advance when a checkpoint's L1 proof lands or the L1 finality window
    // passes. Surface the checkpoint number prominently.
    {
      label: "Proven",
      block: provenBlock,
      checkpoint: provenCheckpoint,
      epoch: provenEpoch,
      color: theme.colors.success,
    },
    {
      label: "Finalized",
      block: finalizedBlock,
      checkpoint: finalizedCheckpoint,
      epoch: finalizedEpoch,
      color: theme.colors.success,
    },
  ];

  return (
    <Sizer>
      <Wrapper>
        {stages.map((stage, i) => (
          <span key={stage.label} style={{ display: "contents" }}>
            <Stage active={stage.block > 0} color={stage.color}>
              <StageLabel>{stage.label}</StageLabel>
              <StageBlock color={stage.color}>
                {stage.block > 0 ? `#${stage.block.toLocaleString()}` : "—"}
              </StageBlock>
              <StageMeta>
                {stage.block > 0 && stage.checkpoint != null ? (
                  <>
                    <MetaNoun>Checkpoint </MetaNoun>
                    <MetaNounShort>chk </MetaNounShort>#{stage.checkpoint.toLocaleString()}
                  </>
                ) : (
                  " "
                )}
              </StageMeta>
            </Stage>
            {i < stages.length - 1 && (
              <Gap
                severity={gapSeverity(
                  stages[i].block - stages[i + 1].block,
                  // The proof-window note speaks for this gap now.
                  stages[i].label === "Checkpointed",
                )}
              >
                {stages[i].block - stages[i + 1].block > 0
                  ? `−${stages[i].block - stages[i + 1].block}`
                  : "→"}
              </Gap>
            )}
          </span>
        ))}
      </Wrapper>
      {note && (
        <Note>
          <span>{note.lead}</span>
          <NoteFigure tone={note.tone}>
            <NoteLong>{note.figureLong}</NoteLong>
            <NoteShort>{note.figureShort}</NoteShort>
          </NoteFigure>
          {note.tailLong !== "" && (
            <span>
              <NoteLong>{note.tailLong}</NoteLong>
              <NoteShort>{note.tailShort}</NoteShort>
            </span>
          )}
        </Note>
      )}
    </Sizer>
  );
}
